import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import {
  Row,
  Col,
  CardHeader,
  CardBody,
  Card,
  Label,
  Form,
  Button,
  CardFooter,
  Input,
} from "reactstrap";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import { commonValidationMessages } from "Components/ValidationErrorMessages";
import apiGlobal from "global/api.global";
import { checkValidArray, handleServerResponse, saveEditForm, useSafeFormikContext } from "GenericForms/Helper";
import ErrorComponent from "Components/ErrorComponent";
import ErrorTooltip from "Components/ErrorTooltip";
import env from "environment_system/env_system";
import { RootState } from "index";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentReportAction } from "Store/Generic/ReportingSlice";
import FormLoading from "Components/FormLoading";
import { VesselMachineryConstant } from "shared/constants";
import { useEditReport } from "VesselReportingInfo/EditCompletedReports/EditReportProvider";

interface EngineParameterComponentType {
  toggleAddTab: any;
  activeAddTab: number;
  toggleDynamicTabs: any;
  setErrorMessage: (value: boolean) => void;
  editReportBool: boolean;
}

const EngineParameterComponent = ({
  toggleAddTab,
  activeAddTab,
  toggleDynamicTabs,
  setErrorMessage,
  editReportBool
}: EngineParameterComponentType) => {
  /** State variables start */
  const { previousEnggTabIndex, discardwarning } = useEditReport();
  const { VesselID, ReportID, VoyageID, CurrentReport, reportingLoading, reportingError, ConfigurationLimits } = useSelector((state: RootState) => state.Reporting);
  const dispatch = useDispatch();
  const voyageParameterCurrentReport = CurrentReport?.data?.chief_engineer?.voyage_parameter ?? null;
  const energyParameterCurrentReport = CurrentReport?.data?.chief_engineer?.energy_parameter ?? null;
  const mainEngineParameterCurrentReport = CurrentReport?.data?.chief_engineer?.lfonds_engine_parameter ?? null;
  const auxEngineParamterCurrentReport = CurrentReport?.data?.chief_engineer?.lfonds_auxiliary_engine_parameter ?? null;
  const [engineCount, setEngineCount] = useState(0);
  const formikContext = useSafeFormikContext<any>();
  const editValues = formikContext?.values;
  const setEditValues = formikContext?.setValues;
  const editInitialValues = formikContext?.initialValues;
  const editSubmit = formikContext?.submitForm;
  const editIsSubmitting = formikContext?.isSubmitting;
  const AuxEngines = ConfigurationLimits?.vessel_machinery_list
    ?.filter((item: any) => item?.machinery_precedence_id === VesselMachineryConstant?.AUXILIARY_ENGINE) || [];
  /** State variables end */

  /** Assign values to initial object of Engine Parameter */
  const getInitialMainEngineValues = () => {
    let obj: any[] = [];
    if (checkValidArray(editInitialValues?.chief_engineer?.lfonds_engine_parameter)) {
      return editInitialValues?.chief_engineer?.lfonds_engine_parameter;
    } else if (checkValidArray(mainEngineParameterCurrentReport)) {
      return mainEngineParameterCurrentReport;
    } else {
      if (checkValidArray(voyageParameterCurrentReport) &&
        obj.length <= voyageParameterCurrentReport.length
      ) {
        voyageParameterCurrentReport.map((machine: any) => {
          obj.push({
            load_indicator: 0,
            engine_rpm: machine.avg_rpm,
            slip_percentage: machine.slip_percentage,
            propeller_pitch: machine.propeller_pitch,
            vessel_machinery_name: machine.vessel_machinery_name,
            output_kw: energyParameterCurrentReport?.find((item: any) => item.vessel_machinery === machine.vessel_machinery)?.avg_power ?? 0,
            percentage_mcr: 0,
            me_exh_temp_max: 0,
            me_exh_temp_min: 0,
            tc_1_inlet_exh_temp: 0,
            tc_1_outlet_exh_temp: 0,
            tc_2_inlet_exh_temp: 0,
            tc_2_outlet_exh_temp: 0,
            vessel_machinery: machine.vessel_machinery,
            vessel: VesselID,
            voyage_information: VoyageID,
            vessel_reporting_information: ReportID,
          });
          return "";
        });
        return obj;
      }
    }
  };

  /** Assign values to initial object of Engine Parameter */
  const getInitialAuxEngineValues = () => {
    let obj: any[] = [];
    if (checkValidArray(editInitialValues?.chief_engineer?.lfonds_auxiliary_engine_parameter)) {
      return editInitialValues?.chief_engineer?.lfonds_auxiliary_engine_parameter;
    } else if (checkValidArray(auxEngineParamterCurrentReport)) {
      return auxEngineParamterCurrentReport;
    } else {
      if (obj !== undefined && AuxEngines !== undefined && obj.length <= AuxEngines.length) {
        AuxEngines.forEach((aux: any) => {
          obj.push({
            vessel_machinery: aux.id,
            vessel_machinery_name: aux.vessel_machinery_name,
            alternator_load: 0,
            ae_avg_exh_temp: 0,
            ae_scavenge_pressure: 0,
            vessel: VesselID,
            voyage_information: VoyageID,
            vessel_reporting_information: ReportID,
          });
        });
        return obj;
      }
    }
  };

  /** Engine parameter's formik object */
  const EngineParameterFormik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      mainEngineParameter: getInitialMainEngineValues(),
      auxEngineParamter: getInitialAuxEngineValues(),
    },
    validationSchema: Yup.object().shape({
      mainEngineParameter: Yup.array(
        Yup.object({
          load_indicator: Yup.string()
            .matches(
              /^\d{0,3}(?:\.\d{1,4})?$/,
              `${commonValidationMessages.before3after4}`
            )
            .test(
              "max-value",
              `${commonValidationMessages.maxVal100}`,
              (value) => parseFloat(value) <= 100.0
            ),
          percentage_mcr: Yup.string().matches(
            /^\d{0,3}(?:\.\d{1,4})?$/,
            `${commonValidationMessages.before3after4}`
          ),
          me_exh_temp_max: Yup.string().matches(
            /^\d{0,3}(\.\d{1,4})?$/,
            `${commonValidationMessages.before3after4}`
          ),
          me_exh_temp_min: Yup.string().matches(
            /^\d{0,3}(\.\d{1,4})?$/,
            `${commonValidationMessages.before3after4}`
          ),
          tc_1_inlet_exh_temp: Yup.string().matches(
            /^\d{0,3}(\.\d{1,4})?$/,
            `${commonValidationMessages.before3after4}`
          ),
          tc_1_outlet_exh_temp: Yup.string().matches(
            /^\d{0,3}(\.\d{1,4})?$/,
            `${commonValidationMessages.before3after4}`
          ),
          tc_2_inlet_exh_temp: Yup.string().matches(
            /^\d{0,3}(\.\d{1,4})?$/,
            `${commonValidationMessages.before3after4}`
          ),
          tc_2_outlet_exh_temp: Yup.string().matches(
            /^\d{0,3}(\.\d{1,4})?$/,
            `${commonValidationMessages.before3after4}`
          ),
        })
      ),
    }),
    onSubmit: async (values: any, actions: any) => {
      if (env?.form_validation === false) {
        setErrorMessage(null)
        toggleAddTab(activeAddTab + 1);
      }
      actions.setSubmitting(true);
      let responseArray: any = [];
      if (checkValidArray(voyageParameterCurrentReport)) {
        if (checkValidArray(mainEngineParameterCurrentReport)) {
          values.mainEngineParameter.forEach((engine: any) => {
            responseArray.push(apiGlobal.put(`/lfonds_engine_parameter/${engine.id}/`, engine));
          });
        } else {
          responseArray.push(apiGlobal.post(`/lfonds_engine_parameter/`, values.mainEngineParameter));
        }
      }
      if (checkValidArray(auxEngineParamterCurrentReport)) {
        values.auxEngineParamter.forEach((engine: any) => {
          responseArray.push(apiGlobal.put(`/lfonds_auxiliary_engine_parameter/${engine.id}/`, engine));
        });
      } else {
        responseArray.push(apiGlobal.post(`/lfonds_auxiliary_engine_parameter/`, values.auxEngineParamter));
      }
      await handleServerResponse(responseArray).then(async (res: any) => {
        if (res === true) {
          dispatch(getCurrentReportAction(VesselID, ReportID));
          setEngineCount(engineCount + 1);
          if (env?.form_validation === true) {
            setErrorMessage(null)
            toggleAddTab(activeAddTab + 1);
          }
        } else {
          setErrorMessage(res)
        }
        actions.setSubmitting(false);
      })
    }
  });

  /** useEffect */
  useEffect(() => {
    EngineParameterFormik.initialValues.mainEngineParameter = getInitialMainEngineValues();
    setEngineCount(engineCount + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voyageParameterCurrentReport, mainEngineParameterCurrentReport, energyParameterCurrentReport]);
  useEffect(() => {
    EngineParameterFormik.initialValues.auxEngineParamter = getInitialAuxEngineValues();
    setEngineCount(engineCount + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ConfigurationLimits, auxEngineParamterCurrentReport]);
  useEffect(() => {
    if (editValues?.chief_engineer) {
      setEditValues({
        ...editValues,
        chief_engineer: {
          ...editValues?.chief_engineer,
          lfonds_engine_parameter: EngineParameterFormik?.values?.mainEngineParameter,
          lfonds_auxiliary_engine_parameter: EngineParameterFormik?.values?.auxEngineParamter,
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [EngineParameterFormik?.values])
  /** useEffect end */

  return (
    <React.Fragment>
      <CardHeader className="p-2">
        <div className="text-center mb-2">
          <Row>
            <Col>
              <h4 className="page_title pos-start mb-0">Engine Parameters</h4>
              <p className="card-title-desc pos-start">
                All readings since last report
              </p>
            </Col>
          </Row>
        </div>
      </CardHeader>
      {reportingLoading && <FormLoading />}
      {reportingError && <ErrorComponent message="Unable to load required data!" />}
      {!reportingLoading && !reportingError && (
        <Form autoComplete="off" onSubmit={EngineParameterFormik?.handleSubmit} noValidate key={engineCount}>
          <React.Fragment>
            {EngineParameterFormik?.values &&
              EngineParameterFormik?.values?.mainEngineParameter?.map(
                (engine: any, index: number) => {
                  return (
                    <Card className="mb-0 border-0">
                      <CardHeader className="p-2">
                        <h5>{engine.vessel_machinery_name}</h5>
                      </CardHeader>
                      <CardBody className="p-2">
                        <Row className="mb-3">
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`load_indicator_${index}`}
                            >
                              Load indicator
                            </Label>
                            <Input
                              onFocus={(e) => e.target.select()}
                              name={`mainEngineParameter.${index}.load_indicator`}
                              type="text"
                              className="form-control text-right max-width-7"
                              id={`load_indicator_${index}`}
                              onBlur={(e: any) => EngineParameterFormik?.handleChange(e)}
                              defaultValue={EngineParameterFormik?.values?.mainEngineParameter[index]?.load_indicator}
                            />
                            {EngineParameterFormik?.errors?.mainEngineParameter &&
                              EngineParameterFormik?.errors?.mainEngineParameter[index].load_indicator &&
                              env?.form_validation === true && !discardwarning && (
                                <ErrorTooltip
                                  target={`load_indicator_${index}`}
                                  message={
                                    EngineParameterFormik?.errors?.mainEngineParameter[index].load_indicator
                                  }
                                  open={
                                    EngineParameterFormik?.errors?.mainEngineParameter &&
                                      EngineParameterFormik?.errors?.mainEngineParameter[index].load_indicator
                                      ? true
                                      : false
                                  }
                                />
                              )}
                          </Col>
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`mainEngineParameter.${index}.slip_percentage`}
                            >
                              Engine RPM
                            </Label>
                            <Input
                              onFocus={(e) => e.target.select()}
                              name={`mainEngineParameter.${index}.engine_rpm`}
                              type="text"
                              className="form-control text-right max-width-7"
                              id={`mainEngineParameter.${index}.engine_rpm`}
                              value={engine.engine_rpm}
                              disabled
                            />
                          </Col>
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`mainEngineParameter.${index}.slip_percentage`}
                            >
                              Slip percentage
                            </Label>
                            <Input
                              onFocus={(e) => e.target.select()}
                              name={`mainEngineParameter.${index}.slip_percentage`}
                              type="text"
                              className="form-control text-right max-width-7"
                              id={`mainEngineParameter.${index}.slip_percentage`}
                              value={engine.slip_percentage}
                              disabled
                            />
                          </Col>
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`mainEngineParameter.${index}.propeller_pitch`}
                            >
                              Propeller pitch
                            </Label>
                            <Input
                              onFocus={(e) => e.target.select()}
                              name={`mainEngineParameter.${index}.propeller_pitch`}
                              type="text"
                              className="form-control text-right max-width-7"
                              id={`mainEngineParameter.${index}.propeller_pitch`}
                              value={engine.propeller_pitch}
                              disabled
                            />
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`output_kw-${index}`}
                            >
                              Output
                            </Label>
                            <div className="input-group">
                              <Input
                                onFocus={(e) => e.target.select()}
                                name={`mainEngineParameter.${index}.output_kw`}
                                type="text"
                                className="form-control text-right max-width-7"
                                id={`output_kw-${index}`}
                                value={EngineParameterFormik?.values?.mainEngineParameter[index].output_kw}
                                disabled
                              />
                              <div className="input-group-text round_border">
                                kW
                              </div>
                            </div>
                            {EngineParameterFormik?.errors?.mainEngineParameter &&
                              EngineParameterFormik?.errors?.mainEngineParameter[index]
                                .output_kw &&
                              env?.form_validation === true && !discardwarning && (
                                <ErrorTooltip
                                  target={`output_kw-${index}`}
                                  message={
                                    EngineParameterFormik?.errors?.mainEngineParameter[index]
                                      .output_kw
                                  }
                                  open={
                                    EngineParameterFormik?.errors?.mainEngineParameter &&
                                      EngineParameterFormik?.errors?.mainEngineParameter[index]
                                        .output_kw
                                      ? true
                                      : false
                                  }
                                />
                              )}
                          </Col>
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`percentage_mcr_${index}`}
                            >
                              Percentage MCR
                            </Label>
                            <div className="input-group">
                              <Input
                                onFocus={(e) => e.target.select()}
                                name={`mainEngineParameter.${index}.percentage_mcr`}
                                type="text"
                                className="form-control text-right max-width-7"
                                id={`percentage_mcr_${index}`}
                                onBlur={(e: any) => EngineParameterFormik?.handleChange(e)}
                                defaultValue={EngineParameterFormik?.values?.mainEngineParameter[index]?.percentage_mcr}
                              />
                              <div className="input-group-text round_border">
                                %
                              </div>
                            </div>
                            {EngineParameterFormik?.errors?.mainEngineParameter &&
                              EngineParameterFormik?.errors?.mainEngineParameter[index]
                                ?.percentage_mcr &&
                              env?.form_validation === true && !discardwarning && (
                                <ErrorTooltip
                                  target={`percentage_mcr_${index}`}
                                  message={
                                    EngineParameterFormik?.errors?.mainEngineParameter[index]
                                      ?.percentage_mcr
                                  }
                                  open={
                                    EngineParameterFormik?.errors?.mainEngineParameter &&
                                      EngineParameterFormik?.errors?.mainEngineParameter[index]
                                        ?.percentage_mcr
                                      ? true
                                      : false
                                  }
                                />
                              )}
                          </Col>
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`me_exh_temp_max_${index}`}
                            >
                              M/E exh. temp. max
                            </Label>
                            <div className="input-group">
                              <Input
                                onFocus={(e) => e.target.select()}
                                name={`mainEngineParameter.${index}.me_exh_temp_max`}
                                type="text"
                                className="form-control text-right max-width-7"
                                id={`me_exh_temp_max_${index}`}
                                onBlur={(e: any) => EngineParameterFormik?.handleChange(e)}
                                defaultValue={EngineParameterFormik?.values?.mainEngineParameter[index]?.me_exh_temp_max}
                              />
                              <div className="input-group-text round_border">
                                <sup>o</sup>C
                              </div>
                            </div>
                            {EngineParameterFormik?.errors?.mainEngineParameter &&
                              EngineParameterFormik?.errors?.mainEngineParameter[index]
                                ?.me_exh_temp_max &&
                              env?.form_validation === true && !discardwarning && (
                                <ErrorTooltip
                                  target={`me_exh_temp_max_${index}`}
                                  message={
                                    EngineParameterFormik?.errors?.mainEngineParameter[index]
                                      ?.me_exh_temp_max
                                  }
                                  open={
                                    EngineParameterFormik?.errors?.mainEngineParameter &&
                                      EngineParameterFormik?.errors?.mainEngineParameter[index]
                                        ?.me_exh_temp_max
                                      ? true
                                      : false
                                  }
                                />
                              )}
                          </Col>
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`me_exh_temp_min_${index}`}
                            >
                              M/E exh. temp. min
                            </Label>
                            <div className="input-group">
                              <Input
                                onFocus={(e) => e.target.select()}
                                name={`mainEngineParameter.${index}.me_exh_temp_min`}
                                type="text"
                                className="form-control text-right max-width-7"
                                id={`me_exh_temp_min_${index}`}
                                onBlur={(e: any) => EngineParameterFormik?.handleChange(e)}
                                defaultValue={EngineParameterFormik?.values?.mainEngineParameter[index]?.me_exh_temp_min}
                              />
                              <div className="input-group-text round_border">
                                <sup>o</sup>C
                              </div>
                            </div>
                            {EngineParameterFormik?.errors?.mainEngineParameter &&
                              EngineParameterFormik?.errors?.mainEngineParameter[index]
                                ?.me_exh_temp_min &&
                              env?.form_validation === true && !discardwarning && (
                                <ErrorTooltip
                                  target={`me_exh_temp_min_${index}`}
                                  message={
                                    EngineParameterFormik?.errors?.mainEngineParameter[index]
                                      ?.me_exh_temp_min
                                  }
                                  open={
                                    EngineParameterFormik?.errors?.mainEngineParameter &&
                                      EngineParameterFormik?.errors?.mainEngineParameter[index]
                                        ?.me_exh_temp_min
                                      ? true
                                      : false
                                  }
                                />
                              )}
                          </Col>
                        </Row>
                        <Row className="mb-3">
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`tc_1_inlet_exh_temp_${index}`}
                            >
                              T/C 1 inlet exh. temp.
                            </Label>
                            <div className="input-group">
                              <Input
                                onFocus={(e) => e.target.select()}
                                name={`mainEngineParameter.${index}.tc_1_inlet_exh_temp`}
                                type="text"
                                className="form-control text-right max-width-7"
                                id={`tc_1_inlet_exh_temp_${index}`}
                                onBlur={(e: any) => EngineParameterFormik?.handleChange(e)}
                                defaultValue={EngineParameterFormik?.values?.mainEngineParameter[index]?.tc_1_inlet_exh_temp}
                              />
                              <div className="input-group-text round_border">
                                <sup>o</sup>C
                              </div>
                            </div>
                            {EngineParameterFormik?.errors?.mainEngineParameter &&
                              EngineParameterFormik?.errors?.mainEngineParameter[index]
                                ?.tc_1_inlet_exh_temp &&
                              env?.form_validation === true && !discardwarning && (
                                <ErrorTooltip
                                  target={`tc_1_inlet_exh_temp_${index}`}
                                  message={
                                    EngineParameterFormik?.errors?.mainEngineParameter[index]
                                      ?.tc_1_inlet_exh_temp
                                  }
                                  open={
                                    EngineParameterFormik?.errors?.mainEngineParameter &&
                                      EngineParameterFormik?.errors?.mainEngineParameter[index]
                                        ?.tc_1_inlet_exh_temp
                                      ? true
                                      : false
                                  }
                                />
                              )}
                          </Col>
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`tc_1_outlet_exh_temp_${index}`}
                            >
                              T/C 1 outlet exh. temp.
                            </Label>
                            <div className="input-group">
                              <Input
                                onFocus={(e) => e.target.select()}
                                name={`mainEngineParameter.${index}.tc_1_outlet_exh_temp`}
                                type="text"
                                className="form-control text-right max-width-7"
                                id={`tc_1_outlet_exh_temp_${index}`}
                                onBlur={(e: any) => EngineParameterFormik?.handleChange(e)}
                                defaultValue={EngineParameterFormik?.values?.mainEngineParameter[index]?.tc_1_outlet_exh_temp}
                              />
                              <div className="input-group-text round_border">
                                <sup>o</sup>C
                              </div>
                            </div>
                            {EngineParameterFormik?.errors?.mainEngineParameter &&
                              EngineParameterFormik?.errors?.mainEngineParameter[index]
                                ?.tc_1_outlet_exh_temp &&
                              env?.form_validation === true && !discardwarning && (
                                <ErrorTooltip
                                  target={`tc_1_outlet_exh_temp_${index}`}
                                  message={
                                    EngineParameterFormik?.errors?.mainEngineParameter[index]
                                      ?.tc_1_outlet_exh_temp
                                  }
                                  open={
                                    EngineParameterFormik?.errors?.mainEngineParameter &&
                                      EngineParameterFormik?.errors?.mainEngineParameter[index]
                                        ?.tc_1_outlet_exh_temp
                                      ? true
                                      : false
                                  }
                                />
                              )}
                          </Col>
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`tc_2_inlet_exh_temp_${index}`}
                            >
                              T/C 2 inlet exh. temp.
                            </Label>
                            <div className="input-group">
                              <Input
                                onFocus={(e) => e.target.select()}
                                name={`mainEngineParameter.${index}.tc_2_inlet_exh_temp`}
                                type="text"
                                className="form-control text-right max-width-7"
                                id={`tc_2_inlet_exh_temp_${index}`}
                                onBlur={(e: any) => EngineParameterFormik?.handleChange(e)}
                                defaultValue={EngineParameterFormik?.values?.mainEngineParameter[index]?.tc_2_inlet_exh_temp}
                              />
                              <div className="input-group-text round_border">
                                <sup>o</sup>C
                              </div>
                            </div>
                            {EngineParameterFormik?.errors?.mainEngineParameter &&
                              EngineParameterFormik?.errors?.mainEngineParameter[index]
                                ?.tc_2_inlet_exh_temp &&
                              env?.form_validation === true && !discardwarning && (
                                <ErrorTooltip
                                  target={`tc_2_inlet_exh_temp_${index}`}
                                  message={
                                    EngineParameterFormik?.errors?.mainEngineParameter[index]
                                      ?.tc_2_inlet_exh_temp
                                  }
                                  open={
                                    EngineParameterFormik?.errors?.mainEngineParameter &&
                                      EngineParameterFormik?.errors?.mainEngineParameter[index]
                                        ?.tc_2_inlet_exh_temp
                                      ? true
                                      : false
                                  }
                                />
                              )}
                          </Col>
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`tc_2_outlet_exh_temp_${index}`}
                            >
                              T/C 2 outlet exh. temp.
                            </Label>
                            <div className="input-group">
                              <Input
                                onFocus={(e) => e.target.select()}
                                name={`mainEngineParameter.${index}.tc_2_outlet_exh_temp`}
                                type="text"
                                className="form-control text-right max-width-7"
                                id={`tc_2_outlet_exh_temp_${index}`}
                                onBlur={(e: any) => EngineParameterFormik?.handleChange(e)}
                                defaultValue={EngineParameterFormik?.values?.mainEngineParameter[index]?.tc_2_outlet_exh_temp}
                              />
                              <div className="input-group-text round_border">
                                <sup>o</sup>C
                              </div>
                            </div>
                            {EngineParameterFormik?.errors?.mainEngineParameter &&
                              EngineParameterFormik?.errors?.mainEngineParameter[index]
                                ?.tc_2_outlet_exh_temp &&
                              env?.form_validation === true && !discardwarning && (
                                <ErrorTooltip
                                  target={`tc_2_outlet_exh_temp_${index}`}
                                  message={
                                    EngineParameterFormik?.errors?.mainEngineParameter[index]
                                      ?.tc_2_outlet_exh_temp
                                  }
                                  open={
                                    EngineParameterFormik?.errors?.mainEngineParameter &&
                                      EngineParameterFormik?.errors?.mainEngineParameter[index]
                                        ?.tc_2_outlet_exh_temp
                                      ? true
                                      : false
                                  }
                                />
                              )}
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  );
                }
              )}
            {EngineParameterFormik?.values &&
              EngineParameterFormik?.values?.auxEngineParamter?.map(
                (engine: any, index: number) => {
                  return (
                    <Card className="mb-0 rounded-0">
                      <CardHeader className="p-2">
                        <h5>{engine.vessel_machinery_name}</h5>
                      </CardHeader>
                      <CardBody className="p-2">
                        <Row className="mb-3">
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`alternator_load_${index}`}
                            >
                              Alternator load
                            </Label>
                            <div className="input-group">
                              <Input
                                onFocus={(e) => e.target.select()}
                                type="text"
                                id={`alternator_load_${index}`}
                                name={`auxEngineParamter.${index}.alternator_load`}
                                className="form-control text-right max-width-7"
                                onBlur={(e: any) => EngineParameterFormik?.handleChange(e)}
                                defaultValue={EngineParameterFormik?.values?.auxEngineParamter[index]?.alternator_load}
                              />
                              <div className="input-group-text">
                                kW
                              </div>
                            </div>
                          </Col>
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`ae_avg_exh_temp_${index}`}
                            >
                              A/E avg exhaust temperature
                            </Label>
                            <div className="input-group">
                              <Input
                                onFocus={(e) => e.target.select()}
                                type="text"
                                id={`ae_avg_exh_temp_${index}`}
                                name={`auxEngineParamter.${index}.ae_avg_exh_temp`}
                                className="form-control text-right max-width-7"
                                onBlur={(e: any) => EngineParameterFormik?.handleChange(e)}
                                defaultValue={EngineParameterFormik?.values?.auxEngineParamter[index]?.ae_avg_exh_temp}
                              />
                              <div className="input-group-text">
                                <sup>o</sup>C
                              </div>
                            </div>
                          </Col>
                          <Col lg={3}>
                            <Label
                              className="mb-0"
                              for={`ae_scavenge_pressure_${index}`}
                            >
                              A/E scavenge pressure
                            </Label>
                            <div className="input-group">
                              <Input
                                onFocus={(e) => e.target.select()}
                                type="text"
                                id={`ae_scavenge_pressure_${index}`}
                                name={`auxEngineParamter.${index}.ae_scavenge_pressure`}
                                className="form-control text-right max-width-7"
                                onBlur={(e: any) => EngineParameterFormik?.handleChange(e)}
                                defaultValue={EngineParameterFormik?.values?.auxEngineParamter[index]?.ae_scavenge_pressure}
                              />
                              <div className="input-group-text">
                                kg/cm<sup>2</sup>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  );
                }
              )}
          </React.Fragment>
          <CardFooter className="p-2 py-3">
            <Row className="d-flex align-items-center flex-row-reverse">
              <Col lg={10}>
                <div className="d-flex flex-wrap gap-3 justify-content-end ">
                  {/* {editReportBool && (
                    <Button
                      type="reset"
                      color="danger"
                      className="btn_size_cstm"
                      onClick={() => {
                        setEditValues({
                          ...editValues,
                          deck_officer: {
                            ...editValues?.deck_officer,
                            lfonds_sludge_water_detail: editInitialValues?.deck_officer?.lfonds_sludge_water_detail,
                            lfonds_bilge_water_detail: editInitialValues?.deck_officer?.lfonds_bilge_water_detail,
                          }
                        });
                      }}
                    >
                      Reset
                    </Button>
                  )} */}
                  <Button
                    type={editReportBool ? "button" : "submit"}
                    color="primary"
                    className="btn_size_cstm"
                    disabled={EngineParameterFormik?.isSubmitting || editIsSubmitting}
                    onClick={async () => {
                      const validationErrors = await EngineParameterFormik?.validateForm();
                      Object.keys(EngineParameterFormik?.values || {}).forEach((key: any) => {
                        EngineParameterFormik?.setFieldTouched(key, true);
                      });
                      if (editReportBool && Object.keys(validationErrors).length === 0) {
                        await saveEditForm(editSubmit, toggleAddTab, activeAddTab);
                      }
                    }}
                  >
                    {(EngineParameterFormik?.isSubmitting || editIsSubmitting) && (
                      <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                    )}
                    Next <i className="bx bx-chevron-right ms-1" />
                  </Button>
                </div>
              </Col>
              <Col lg={2}>
                <Button
                  type="button"
                  color="primary"
                  className="btn_size_cstm"
                  onClick={() => {
                    EngineParameterFormik?.setErrors({});
                    setErrorMessage(null);
                    if (activeAddTab === 1) {
                      toggleDynamicTabs(previousEnggTabIndex - 1);
                    } else {
                      toggleAddTab(activeAddTab - 1);
                    }
                  }}
                >
                  <i className="bx bx-chevron-left me-1" /> Previous
                </Button>
              </Col>
            </Row>
          </CardFooter>
          <FormValuesDebug
            values={[EngineParameterFormik?.values, EngineParameterFormik?.errors, EngineParameterFormik.initialValues]}
          />
        </Form>
      )
      }
    </React.Fragment >
  );
};

export default EngineParameterComponent;